import { createContext, useContext, useState, useEffect, useCallback, useRef, type ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";
import { profilesRepository } from "@/repositories";
import { nestGetUser } from "@/services/api";
import { toast } from "@/hooks/use-toast";
import type { ImpersonationSession } from "@/types/subscription";

interface ImpersonationTarget {
  userId: string;
  companyId: string;
  fullName: string;
  fullNameEn?: string;
  companyName: string;
  avatarUrl?: string;
  phone?: string;
  email?: string;
  role?: string;
}

interface ImpersonationContextType {
  isImpersonating: boolean;
  target: ImpersonationTarget | null;
  sessionId: string | null;
  impersonatedCompanyId: string | null;
  startImpersonation: (target: ImpersonationTarget, reason?: string) => Promise<boolean>;
  endImpersonation: () => Promise<void>;
}

const ImpersonationContext = createContext<ImpersonationContextType | null>(null);

const STORAGE_KEY = "impersonation_session";
const EXPIRY_CHECK_INTERVAL_MS = 60_000; // 60 seconds

export function ImpersonationProvider({ children }: { children: ReactNode }) {
  const [isImpersonating, setIsImpersonating] = useState(false);
  const [target, setTarget] = useState<ImpersonationTarget | null>(null);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const clearSession = useCallback(() => {
    localStorage.removeItem(STORAGE_KEY);
    setSessionId(null);
    setTarget(null);
    setIsImpersonating(false);
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    window.dispatchEvent(new CustomEvent('impersonation-changed'));
  }, []);

  /** Verify the session is still active and not expired in the DB */
  const verifySession = useCallback(async (sid: string): Promise<boolean> => {
    const { data, error } = await supabase
      .from("impersonation_sessions")
      .select("id, is_active, expires_at")
      .eq("id", sid)
      .eq("is_active", true)
      .gt("expires_at", new Date().toISOString())
      .maybeSingle();

    return !error && !!data;
  }, []);

  /** Auto-end an expired session with a toast notification */
  const handleSessionExpired = useCallback(async (sid: string) => {
    // Mark as ended in DB
    await supabase
      .from("impersonation_sessions")
      .update({ ended_at: new Date().toISOString(), is_active: false })
      .eq("id", sid);

    clearSession();

    toast({
      title: "انتهت جلسة الانتحال",
      description: "تم إنهاء جلسة الانتحال تلقائياً لانتهاء صلاحيتها.",
      variant: "destructive",
    });
  }, [clearSession]);

  // Restore session from localStorage on mount — with DB validation
  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return;

    let parsed: { sessionId: string; target: ImpersonationTarget };
    try {
      parsed = JSON.parse(stored);
    } catch (err) {
      console.error("[ImpersonationContext] Failed to parse stored session:", err);
      localStorage.removeItem(STORAGE_KEY);
      return;
    }

    // Validate session is still active & not expired
    verifySession(parsed.sessionId).then((valid) => {
      if (valid) {
        setSessionId(parsed.sessionId);
        setTarget(parsed.target);
        setIsImpersonating(true);
      } else {
        // Session expired or deactivated — clean up silently
        localStorage.removeItem(STORAGE_KEY);
      }
    });
  }, [verifySession]);

  // Periodic expiry check while impersonating
  useEffect(() => {
    if (!isImpersonating || !sessionId) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      return;
    }

    intervalRef.current = setInterval(async () => {
      const valid = await verifySession(sessionId);
      if (!valid) {
        await handleSessionExpired(sessionId);
      }
    }, EXPIRY_CHECK_INTERVAL_MS);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [isImpersonating, sessionId, verifySession, handleSessionExpired]);

  const startImpersonation = async (
    impersonationTarget: ImpersonationTarget,
    reason?: string
  ): Promise<boolean> => {
    try {
      const user = await nestGetUser();
      if (!user) return false;

      // Fetch additional profile data for the target user (best-effort
      // enrichment — Supabase transport; D-track/S-5 act-as owns this path)
      const targetProfile = await profilesRepository.getImpersonationTarget(
        impersonationTarget.userId,
      );

      // Get the target user's role
      const { data: targetRoles } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", impersonationTarget.userId)
        .limit(1);

      // Enrich the target with profile data
      const enrichedTarget: ImpersonationTarget = {
        ...impersonationTarget,
        fullName: targetProfile?.full_name || impersonationTarget.fullName,
        fullNameEn: targetProfile?.full_name_en || impersonationTarget.fullNameEn,
        avatarUrl: targetProfile?.avatar_url || impersonationTarget.avatarUrl,
        phone: targetProfile?.phone || impersonationTarget.phone,
        email: targetProfile?.email || impersonationTarget.email,
        role: targetRoles?.[0]?.role || impersonationTarget.role,
      };

      // Create impersonation session record
      const { data, error } = await supabase
        .from("impersonation_sessions")
        .insert({
          impersonator_id: user.id,
          target_user_id: enrichedTarget.userId,
          target_company_id: enrichedTarget.companyId,
          reason: reason || null,
          is_active: true,
        })
        .select()
        .single();

      if (error) throw error;

      const session = data as ImpersonationSession;

      // Store in localStorage for persistence
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({
          sessionId: session.id,
          target: enrichedTarget,
        })
      );

      setSessionId(session.id);
      setTarget(enrichedTarget);
      setIsImpersonating(true);

      // Dispatch custom event to trigger useAuth to update companyId immediately
      window.dispatchEvent(new CustomEvent('impersonation-changed'));

      // Return after a micro-task to ensure event listeners have processed
      await new Promise(resolve => setTimeout(resolve, 0));

      return true;
    } catch (err) {
      console.error("[ImpersonationContext] startImpersonation failed:", err);
      return false;
    }
  };

  const endImpersonation = async () => {
    if (sessionId) {
      // Update session record
      await supabase
        .from("impersonation_sessions")
        .update({
          ended_at: new Date().toISOString(),
          is_active: false,
        })
        .eq("id", sessionId);
    }

    clearSession();
  };

  // Expose impersonated company ID for other components to use
  const impersonatedCompanyId = isImpersonating && target ? target.companyId : null;

  return (
    <ImpersonationContext.Provider
      value={{
        isImpersonating,
        target,
        sessionId,
        impersonatedCompanyId,
        startImpersonation,
        endImpersonation,
      }}
    >
      {children}
    </ImpersonationContext.Provider>
  );
}

export function useImpersonation() {
  const context = useContext(ImpersonationContext);
  if (!context) {
    throw new Error("useImpersonation must be used within ImpersonationProvider");
  }
  return context;
}